import { fileTypeFromBlob } from "file-type";
import {
  ALLOWED_MIME_TYPES,
  FILE_PREVIEW_SUPPORTED_EXTENSIONS,
  ALLOWED_TYPES_DISPLAY,
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_SIZE_MB,
  ATTACHMENT_MAX_FILES_AT_ONCE,
} from "@/constants";
import type {
  FileValidationError,
  DetectedFileType,
  ValidateFilesOptions,
} from "../types";

export const detectFileType = async (
  file: File,
): Promise<DetectedFileType | null> => {
  try {
    const result = await fileTypeFromBlob(file);
    if (!result) {
      return null;
    }
    return { ext: result.ext.toLowerCase(), mime: result.mime };
  } catch {
    return null;
  }
};

export const validateFileSignature = async (
  file: File,
): Promise<FileValidationError | null> => {
  const detected = await detectFileType(file);
  if (
    !detected ||
    !(FILE_PREVIEW_SUPPORTED_EXTENSIONS as readonly string[]).includes(
      detected.ext,
    )
  ) {
    return {
      type: "invalid_signature",
      metadata: {
        filename: file.name,
      },
    };
  }
  return null;
};

export const validateFileType = (file: File): FileValidationError | null => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      type: "invalid_type",
      metadata: {
        filename: file.name,
        allowedTypes: ALLOWED_TYPES_DISPLAY,
      },
    };
  }
  return null;
};

const validateFileSize = (
  file: File,
  maxSizeBytes: number,
  maxSizeMB: number,
): FileValidationError | null => {
  if (file.size > maxSizeBytes) {
    return {
      type: "too_large",
      metadata: {
        filename: file.name,
        maxSize: maxSizeMB,
      },
    };
  }
  return null;
};

const validateFile = async (
  file: File,
  maxSizeBytes: number,
  maxSizeMB: number,
): Promise<FileValidationError | null> => {
  const typeError = validateFileType(file);
  if (typeError) return typeError;

  const sizeError = validateFileSize(file, maxSizeBytes, maxSizeMB);
  if (sizeError) return sizeError;

  const signatureError = await validateFileSignature(file);
  if (signatureError) return signatureError;

  return null;
};

const validateMaxFiles = (
  filesCount: number,
  maxFiles: number,
): FileValidationError | null => {
  if (filesCount > maxFiles) {
    return {
      type: "max_files_exceeded",
      metadata: {
        max: maxFiles,
      },
    };
  }
  return null;
};

export const validateFiles = async (
  files: File[],
  options: ValidateFilesOptions = {},
): Promise<FileValidationError | null> => {
  if (files.length === 0) return null;

  const {
    maxFiles = ATTACHMENT_MAX_FILES_AT_ONCE,
    maxFileSizeBytes = ATTACHMENT_MAX_FILE_SIZE_BYTES,
    maxFileSizeMB = ATTACHMENT_MAX_FILE_SIZE_MB,
  } = options;

  const maxFilesError = validateMaxFiles(files.length, maxFiles);
  if (maxFilesError) return maxFilesError;

  for (const file of files) {
    const error = await validateFile(file, maxFileSizeBytes, maxFileSizeMB);
    if (error) return error;
  }

  return null;
};
